import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { PageLoader } from '@/components/ui/page-loader'
import { Pagination } from '@/components/ui/pagination'
import { useAuthStore } from '@/store/auth-store'
import { opportunitiesService } from '@/services/opportunities-service'
import { companyService } from '@/services/company-service'
import { OpportunitySubmitModal } from '@/components/opportunities/opportunity-submit-modal'
import type { Opportunity, OpportunitySource } from '@/types'

const PAGE_SIZE = 25

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN || ''

/** Haversine distance in miles between two lat/lng pairs */
function haversineMiles(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180
  const R = 3958.8 // Earth radius in miles
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

function formatDistance(miles: number): string {
  if (miles < 1) return '<1 mi'
  if (miles >= 100) return `${Math.round(miles)} mi`
  return `${Math.round(miles)} mi`
}

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function formatCurrency(val?: number | null): string {
  if (val === null || val === undefined) return '—'
  if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`
  if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val)
}

function daysUntilDeadline(dateStr?: string | null): number | null {
  if (!dateStr) return null
  const deadline = new Date(dateStr)
  const today = new Date()
  const diff = deadline.getTime() - today.getTime()
  return Math.ceil(diff / (1000 * 60 * 60 * 24))
}

const MenuDotsIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <circle cx="8" cy="3" r="1.5" />
    <circle cx="8" cy="8" r="1.5" />
    <circle cx="8" cy="13" r="1.5" />
  </svg>
)

const ChevronDownIcon = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M6 9l6 6 6-6" />
  </svg>
)

function getSourceLabel(source: OpportunitySource): string {
  switch (source) {
    case 'sam_gov': return 'SAM.gov'
    case 'pdf_upload': return 'PDF'
    case 'email': return 'Email'
    case 'manual': return 'Manual'
    case 'url': return 'URL'
    default: return 'Other'
  }
}

type ActiveTab = 'queue' | 'assigned'
type SourceFilterType = 'all' | OpportunitySource
type MatchFilterType = 'all' | 'strong' | 'possible' | 'low'

export function OpportunitiesListPage() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const role = user?.role || ''
  const canManage = role === 'SUPERADMIN' || role === 'ADMIN' || role === 'MANAGER'

  const [opportunities, setOpportunities] = useState<Opportunity[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [activeTab, setActiveTab] = useState<ActiveTab>('queue')
  const [sourceFilter, setSourceFilter] = useState<SourceFilterType>('all')
  const [matchFilter, setMatchFilter] = useState<MatchFilterType>('all')
  const [showSubmitModal, setShowSubmitModal] = useState(false)
  const [addDropOpen, setAddDropOpen] = useState(false)
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null)
  const [menuAnchor, setMenuAnchor] = useState<{ top: number; right: number } | null>(null)
  const [sourceDropOpen, setSourceDropOpen] = useState(false)
  const [matchDropOpen, setMatchDropOpen] = useState(false)
  const [companyCoords, setCompanyCoords] = useState<{ lat: number; lng: number } | null>(null)
  const addDropRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const sourceDropRef = useRef<HTMLDivElement>(null)
  const matchDropRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    loadOpportunities()
    loadCompanyCoords()
  }, [])

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (addDropOpen && addDropRef.current && !addDropRef.current.contains(e.target as Node)) {
        setAddDropOpen(false)
      }
      if (menuOpenId && menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpenId(null)
        setMenuAnchor(null)
      }
      if (sourceDropOpen && sourceDropRef.current && !sourceDropRef.current.contains(e.target as Node)) {
        setSourceDropOpen(false)
      }
      if (matchDropOpen && matchDropRef.current && !matchDropRef.current.contains(e.target as Node)) {
        setMatchDropOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [addDropOpen, menuOpenId, sourceDropOpen, matchDropOpen])

  async function loadOpportunities() {
    try {
      setLoading(true)
      const data = await opportunitiesService.list()
      setOpportunities(data)
    } catch (err) {
      console.error('Failed to load opportunities:', err)
    } finally {
      setLoading(false)
    }
  }

  async function loadCompanyCoords() {
    try {
      const branding = await companyService.getSettings()
      const parts = [branding.companyAddress, branding.companyCity, branding.companyState, branding.companyZip].filter(Boolean)
      const addr = parts.join(', ')
      if (!addr || !MAPBOX_TOKEN) return
      const res = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(addr)}.json?access_token=${MAPBOX_TOKEN}&limit=1`)
      const data = await res.json()
      if (data.features?.[0]?.center) {
        const [lng, lat] = data.features[0].center
        setCompanyCoords({ lat, lng })
      }
    } catch (err) {
      console.error('Failed to load company coords:', err)
    }
  }

  async function handleDismiss(e: React.MouseEvent, oppId: string) {
    e.stopPropagation()
    setMenuOpenId(null)
    setMenuAnchor(null)
    try {
      await opportunitiesService.dismiss(oppId)
      setOpportunities((prev) => prev.filter((o) => o.id !== oppId))
    } catch (err) {
      console.error('Failed to dismiss opportunity:', err)
    }
  }

  async function handleConvertToRfp(e: React.MouseEvent, oppId: string) {
    e.stopPropagation()
    setMenuOpenId(null)
    setMenuAnchor(null)
    try {
      await opportunitiesService.convertToRfp(oppId)
      setOpportunities((prev) =>
        prev.map((o) => (o.id === oppId ? { ...o, status: 'converted' } : o))
      )
    } catch (err) {
      console.error('Failed to convert to RFP:', err)
    }
  }

  function handleRowClick(oppId: string) {
    navigate(`/opportunities/${oppId}`)
  }

  // Split into queue (unassigned, not dismissed) and assigned (has assignee, not dismissed)
  const nonDismissed = opportunities.filter((o) => o.status !== 'dismissed')
  const queueAll = nonDismissed.filter((o) => !o.assignedToUserId)
  const assignedAll = nonDismissed.filter((o) => !!o.assignedToUserId)

  // Apply search + dropdown filters to whichever tab is active
  const applyFilters = (list: Opportunity[]) => list.filter((o) => {
    if (sourceFilter !== 'all' && o.source !== sourceFilter) return false
    if (matchFilter !== 'all') {
      const conf = o.confidenceScore ?? o.matchScore
      const confTier = conf != null ? (conf >= 0.65 ? 'strong' : conf >= 0.35 ? 'possible' : 'low') : null
      if (confTier !== matchFilter) return false
    }
    if (search) {
      const q = search.toLowerCase()
      if (!o.title.toLowerCase().includes(q)
        && !(o.projectAddress || '').toLowerCase().includes(q)
        && !(o.contactName || '').toLowerCase().includes(q)
        && !(o.assignedToUser?.displayName || '').toLowerCase().includes(q)) return false
    }
    return true
  })

  const filtered = applyFilters(activeTab === 'queue' ? queueAll : assignedAll)
  const queueCount = queueAll.length

  const [page, setPage] = useState(1)
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
  const pagedFiltered = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const sourceLabels: Record<string, string> = {
    all: 'All Sources',
    sam_gov: 'SAM.gov',
    pdf_upload: 'PDF Upload',
    email: 'Email',
    manual: 'Manual',
  }

  const matchLabels: Record<string, string> = {
    all: 'All Confidence',
    strong: 'High',
    possible: 'Moderate',
    low: 'Low',
  }

  /* ── Estimator view: simple assigned-to-me list ── */
  if (role === 'ESTIMATOR') {
    const myOpps = opportunities.filter((o) =>
      !search || o.title.toLowerCase().includes(search.toLowerCase()) || (o.projectAddress || '').toLowerCase().includes(search.toLowerCase())
    )

    return (
      <div className="opp-page">
        <div className="opp-page-header">
          <div>
            <h1 className="opp-page-title">Opportunity Assignments</h1>
            <p className="opp-page-subtitle">Opportunities assigned to you — review the brief and start your estimate</p>
          </div>
        </div>

        {/* Search */}
        <div className="opp-toolbar" style={{ marginBottom: 16 }}>
          <div className="opp-search-wrap">
            <svg className="opp-search-icon" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
            </svg>
            <input type="text" placeholder="Search by project or address..." value={search} onChange={(e) => setSearch(e.target.value)} className="opp-search-input" />
          </div>
        </div>

        {loading ? (
          <PageLoader />
        ) : myOpps.length === 0 ? (
          <div className="opp-empty">
            <svg className="opp-empty-icon" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
              <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2" />
            </svg>
            <p className="opp-empty-title">No assignments yet</p>
            <p className="opp-empty-sub">When a manager assigns you an opportunity it will appear here.</p>
          </div>
        ) : (
          <div className="opp-table-wrap">
            <table className="opp-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Assigned By</th>
                  <th className="opp-th-deadline">Due Date</th>
                </tr>
              </thead>
              <tbody>
                {myOpps.map((opp) => (
                  <tr key={opp.id} className="opp-row" onClick={() => navigate(`/opportunities/${opp.id}`)}>
                    <td>
                      <div className="opp-title-main">{opp.title}</div>
                    </td>
                    <td>{(opp as any).createdByUser?.displayName || '—'}</td>
                    <td>
                      {opp.dueDate ? (
                        <span>{formatDate(opp.dueDate)}</span>
                      ) : <span className="opp-no-date">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="opp-page">
      {/* Header */}
      <div className="opp-page-header">
        <div>
          <h1 className="opp-page-title">Opportunities</h1>
          <p className="opp-page-subtitle">Track and convert leads from government bids, emails, and more</p>
        </div>
        {canManage && (
          <div className="opp-add-wrap" ref={addDropRef}>
            <button className="opp-btn-primary opp-add-btn" onClick={() => setAddDropOpen(o => !o)}>
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M12 5v14m-7-7h14"/></svg>
              Add Opportunity
              <svg className={`opp-add-chevron ${addDropOpen ? 'opp-add-chevron-open' : ''}`} width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
            {addDropOpen && (
              <div className="opp-add-dropdown">
                <button className="opp-add-option" onClick={() => { setAddDropOpen(false); navigate('/opportunities/new?source=manual') }}>
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                  Manual Entry
                </button>
                <button className="opp-add-option" onClick={() => { setAddDropOpen(false); navigate('/opportunities/new?source=pdf') }}>
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="12" y2="17"/></svg>
                  From PDF
                </button>
                <button className="opp-add-option" onClick={() => { setAddDropOpen(false); navigate('/opportunities/new?source=url') }}>
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                  From URL
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Queue / Assigned Tabs */}
      <div className="opp-tabs">
        <button className={`opp-tab ${activeTab === 'queue' ? 'active' : ''}`} onClick={() => { setActiveTab('queue'); setSourceFilter('all'); setMatchFilter('all'); setSearch(''); setPage(1) }}>
          Queue
          {queueCount > 0 && <span className="opp-tab-count opp-tab-count-new">{queueCount}</span>}
        </button>
        <button className={`opp-tab ${activeTab === 'assigned' ? 'active' : ''}`} onClick={() => { setActiveTab('assigned'); setSourceFilter('all'); setMatchFilter('all'); setSearch(''); setPage(1) }}>
          Assigned
        </button>
      </div>

      {/* Toolbar: Search + Dropdown Filters */}
      <div className="opp-toolbar">
        <div className="opp-search-wrap">
          <svg className="opp-search-icon" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder="Search projects, addresses, contacts..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            className="opp-search-input"
          />
        </div>

        <div className="opp-toolbar-filters">
          {/* Source Dropdown */}
          <div className="opp-dropdown" ref={sourceDropRef}>
            <button className={`opp-dropdown-trigger ${sourceFilter !== 'all' ? 'active' : ''}`} onClick={() => { setSourceDropOpen(!sourceDropOpen); setMatchDropOpen(false) }}>
              {sourceLabels[sourceFilter] || 'All Sources'}
              <ChevronDownIcon />
            </button>
            {sourceDropOpen && (
              <div className="opp-dropdown-menu">
                {Object.entries(sourceLabels).map(([key, label]) => (
                  <button
                    key={key}
                    className={`opp-dropdown-item ${sourceFilter === key ? 'active' : ''}`}
                    onClick={() => { setSourceFilter(key as SourceFilterType); setSourceDropOpen(false) }}
                  >
                    {key !== 'all' && <span className={`opp-source-dot opp-source-dot-${key}`} />}
                    {label}
                    {sourceFilter === key && (
                      <svg className="opp-dropdown-check" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7"/></svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Match Dropdown */}
          <div className="opp-dropdown" ref={matchDropRef}>
            <button className={`opp-dropdown-trigger ${matchFilter !== 'all' ? 'active' : ''}`} onClick={() => { setMatchDropOpen(!matchDropOpen); setSourceDropOpen(false) }}>
              {matchLabels[matchFilter] || 'All Matches'}
              <ChevronDownIcon />
            </button>
            {matchDropOpen && (
              <div className="opp-dropdown-menu">
                {Object.entries(matchLabels).map(([key, label]) => (
                  <button
                    key={key}
                    className={`opp-dropdown-item ${matchFilter === key ? 'active' : ''}`}
                    onClick={() => { setMatchFilter(key as MatchFilterType); setMatchDropOpen(false) }}
                  >
                    {key !== 'all' && <span className={`opp-match-dot ${key}`} />}
                    {label}
                    {matchFilter === key && (
                      <svg className="opp-dropdown-check" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7"/></svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Clear filters */}
          {(sourceFilter !== 'all' || matchFilter !== 'all' || search) && (
            <button
              className="opp-clear-filters"
              onClick={() => { setSourceFilter('all'); setMatchFilter('all'); setSearch('') }}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <PageLoader />
      ) : filtered.length === 0 ? (
        <div className="opp-empty">
          <svg className="opp-empty-icon" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
            <path d="M21 21l-5.2-5.2m0 0A7.5 7.5 0 105.8 5.8a7.5 7.5 0 0010 10z" />
          </svg>
          <p className="opp-empty-title">
            {opportunities.length === 0
              ? 'No opportunities yet'
              : activeTab === 'queue'
                ? 'No unassigned opportunities'
                : 'No assigned opportunities'}
          </p>
          <p className="opp-empty-sub">
            {opportunities.length === 0
              ? 'Enable the SAM.gov feed in Settings or add one manually.'
              : activeTab === 'queue'
                ? 'New opportunities will appear here as they come in.'
                : 'Assign opportunities from the Queue tab to see them here.'}
          </p>
        </div>
      ) : (
        <div className="opp-table-wrap">
          <table className="opp-table">
            <thead>
              <tr>
                <th className="opp-th-match">Confidence</th>
                <th>Project</th>
                {activeTab === 'assigned' && <th className="opp-th-assignee">Assignee</th>}
                <th className="opp-th-location">Distance</th>
                <th className="opp-th-budget">Budget</th>
                <th className="opp-th-deadline">Deadline</th>
                <th className="opp-th-source">Source</th>
                <th className="opp-th-actions"></th>
              </tr>
            </thead>
            <tbody>
              {pagedFiltered.map((opp) => {
                const daysLeft = daysUntilDeadline(opp.dueDate)
                const isUrgent = daysLeft !== null && daysLeft < 7 && daysLeft >= 0
                const isExpired = daysLeft !== null && daysLeft < 0
                const isNew = ['new', 'pending_analysis', 'analyzed'].includes(opp.status)
                const isConverted = opp.status === 'converted'
                return (
                  <tr
                    key={opp.id}
                    className="opp-row"
                    onClick={() => handleRowClick(opp.id)}
                  >
                    {/* Confidence Score */}
                    <td className="opp-td-match">
                      {opp.confidenceScore != null ? (
                        <span className="opp-match-score-box">
                          <span className={`opp-match-dot ${opp.confidenceScore >= 0.65 ? 'strong' : opp.confidenceScore >= 0.35 ? 'possible' : 'low'}`} />
                          {Math.round(opp.confidenceScore * 100)}%
                        </span>
                      ) : opp.matchScore != null ? (
                        <span className="opp-match-score-box">
                          <span className={`opp-match-dot ${opp.matchTier || ''}`} />
                          {Math.round(opp.matchScore * 100)}%
                        </span>
                      ) : (
                        <span className="opp-match-score-box opp-match-score-na">—</span>
                      )}
                    </td>

                    {/* Project Title */}
                    <td>
                      <div className="opp-title-main">
                        {activeTab === 'queue' && isNew && <span className="opp-new-tag">New</span>}
                        {activeTab === 'assigned' && isConverted && <span className="opp-converted-tag">Converted</span>}
                        {opp.title.replace(/^\[SEED\]\s*/i, '')}
                      </div>
                    </td>

                    {/* Assignee (Assigned tab only) */}
                    {activeTab === 'assigned' && (
                      <td className="opp-td-assignee">
                        {opp.assignedToUser?.displayName || '—'}
                      </td>
                    )}

                    {/* Distance */}
                    <td className="opp-td-distance">
                      {companyCoords && opp.latitude && opp.longitude
                        ? formatDistance(haversineMiles(companyCoords.lat, companyCoords.lng, opp.latitude, opp.longitude))
                        : '—'}
                    </td>

                    {/* Budget */}
                    <td className="opp-td-budget">{opp.estimatedBudget ? formatCurrency(opp.estimatedBudget) : '—'}</td>

                    {/* Deadline */}
                    <td>
                      {opp.dueDate ? (
                        <div className={`opp-deadline ${isUrgent ? 'urgent' : ''} ${isExpired ? 'expired' : ''}`}>
                          <span className="opp-deadline-date">{formatDate(opp.dueDate)}</span>
                          {isUrgent && <span className="opp-deadline-tag">{daysLeft}d left</span>}
                          {isExpired && <span className="opp-deadline-tag">Expired</span>}
                        </div>
                      ) : (
                        <span className="opp-no-date">—</span>
                      )}
                    </td>

                    {/* Source */}
                    <td className="opp-td-source">
                      {getSourceLabel(opp.source)}
                    </td>

                    {/* Actions */}
                    <td className="opp-td-actions">
                      <button
                        className="opp-row-menu-btn"
                        onClick={(e) => {
                          e.stopPropagation()
                          if (menuOpenId === opp.id) {
                            setMenuOpenId(null)
                            setMenuAnchor(null)
                            return
                          }
                          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
                          setMenuAnchor({
                            top: rect.bottom + 4,
                            right: window.innerWidth - rect.right,
                          })
                          setMenuOpenId(opp.id)
                        }}
                      >
                        <MenuDotsIcon />
                      </button>
                      {menuOpenId === opp.id && menuAnchor && createPortal(
                        <div
                          className="opp-row-menu"
                          ref={menuRef}
                          style={{ position: 'fixed', top: menuAnchor.top, right: menuAnchor.right }}
                        >
                          <button
                            className="opp-row-menu-item"
                            onClick={(e) => { e.stopPropagation(); handleRowClick(opp.id) }}
                          >
                            View Details
                          </button>
                          {opp.status !== 'converted' && opp.status !== 'dismissed' && (
                            <button
                              className="opp-row-menu-item"
                              onClick={(e) => handleConvertToRfp(e, opp.id)}
                            >
                              Convert to RFP
                            </button>
                          )}
                          {opp.status !== 'dismissed' && (
                            <button
                              className="opp-row-menu-item opp-row-menu-item-danger"
                              onClick={(e) => handleDismiss(e, opp.id)}
                            >
                              Dismiss
                            </button>
                          )}
                        </div>,
                        document.body,
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <Pagination
            page={page}
            totalPages={totalPages}
            totalItems={filtered.length}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </div>
      )}

      {showSubmitModal && (
        <OpportunitySubmitModal
          onClose={() => setShowSubmitModal(false)}
          onCreated={() => {
            setShowSubmitModal(false)
            loadOpportunities()
          }}
        />
      )}
    </div>
  )
}
